/**
 * Design Token System
 *
 * Central source of truth for all design values in the application.
 * Replaces hardcoded values with semantic tokens for consistency and maintainability.
 *
 * Migration from inline styles (AIF-22, AIF-24):
 * - 144 hardcoded color instances → design tokens
 * - 637 inline style properties → token references
 *
 * Usage:
 * import { colors, spacing, typography, shadows, radius } from '../theme/tokens';
 * sx={{ backgroundColor: colors.primary.main, padding: spacing.md }}
 */

/**
 * COLOR TOKENS
 * Updated for educational/kindergarten context with softer, more approachable colors
 * Migration: AIF-63 - Softer Educational Color Palette
 */
export const colors = {
  // Primary brand colors (softer, educational tone)
  primary: {
    main: '#1324a1',        // Softer blue - friendly and approachable
    dark: '#4a69b3',        // Darker blue for hover states
    light: '#7a97d9',       // Lighter blue for backgrounds
    contrast: '#ffffff',    // White text on primary
  },

  // Secondary brand colors (warm, welcoming)
  secondary: {
    main: '#7ec8e3',        // Soft sky blue - calm and peaceful
    dark: '#5db5d4',        // Darker sky blue
    light: '#a3d9ed',       // Lighter sky blue
    contrast: '#ffffff',    // White text on secondary
  },

  // Semantic colors
  success: {
    main: '#10b981',        // Green for success states
    dark: '#059669',
    light: '#34d399',
    bg: '#d1fae5',          // Light background
    border: '#6ee7b7',
    contrast: '#ffffff',
  },

  error: {
    main: '#ef4444',        // Red for errors
    dark: '#dc2626',
    light: '#f87171',
    bg: '#fef2f2',          // Light background
    border: '#fecaca',
    contrast: '#ffffff',
  },

  warning: {
    main: '#f59e0b',        // Orange for warnings
    dark: '#d97706',
    light: '#fbbf24',
    bg: '#fef3c7',          // Light background
    border: '#fde68a',
    contrast: '#000000',
  },

  info: {
    main: '#3b82f6',        // Blue for info
    dark: '#2563eb',
    light: '#60a5fa',
    bg: '#dbeafe',          // Light background
    border: '#93c5fd',
    contrast: '#ffffff',
  },

  // Neutral/Gray scale
  neutral: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    black: '#000000',
    white: '#ffffff',
  },

  // Slate scale (for text)
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Background colors
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    subtle: '#f7f8fa',      // Light gray background
    dark: '#f5f7fa',
    disabled: '#f3f4f6',
  },

  // Text colors
  text: {
    primary: '#1e293b',     // Dark slate for primary text (WCAG AAA: 15.8:1 on white)
    secondary: '#475569',   // Darker slate for secondary text (WCAG AA: 8.59:1 on white)
    muted: '#64748b',       // Medium slate for less emphasis (WCAG AA: 5.74:1 on white)
    disabled: '#94a3b8',    // Light slate for disabled states (intentionally lower contrast)
    inverse: '#ffffff',     // White text on dark backgrounds
    link: '#1324a1',        // Primary color for links
  },

  // Border colors
  border: {
    light: '#e5e7eb',
    main: '#d1d5db',
    dark: '#9ca3af',
    focus: '#1324a1',       // Primary color for focus states
  },

  // State colors
  state: {
    hover: 'rgba(19, 36, 161, 0.04)',      // Primary with 4% opacity
    active: 'rgba(19, 36, 161, 0.12)',     // Primary with 12% opacity
    focus: 'rgba(19, 36, 161, 0.12)',      // Primary with 12% opacity
    disabled: 'rgba(0, 0, 0, 0.12)',       // Black with 12% opacity
    selected: 'rgba(19, 36, 161, 0.08)',   // Primary with 8% opacity
  },

  // Transparent overlays
  overlay: {
    light: 'rgba(0, 0, 0, 0.04)',
    medium: 'rgba(0, 0, 0, 0.08)',
    dark: 'rgba(0, 0, 0, 0.12)',
    darker: 'rgba(0, 0, 0, 0.24)',
  },
};

/**
 * SPACING TOKENS
 * Based on 4px base unit (common in Material Design)
 */
export const spacing = {
  xs: '4px',      // 0.25rem
  sm: '8px',      // 0.5rem
  md: '16px',     // 1rem
  lg: '24px',     // 1.5rem
  xl: '32px',     // 2rem
  '2xl': '48px',  // 3rem
  '3xl': '64px',  // 4rem
  '4xl': '96px',  // 6rem

  // Semantic spacing
  gutter: '16px',         // Default spacing between elements
  section: '48px',        // Spacing between sections
  component: '24px',      // Spacing within components
  inline: '8px',          // Spacing for inline elements
};

/**
 * TYPOGRAPHY TOKENS
 * Font sizes, weights, and line heights
 */
export const typography = {
  // Font families
  fontFamily: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    hebrew: '"Alef", "David Libre", sans-serif',
    monospace: '"Courier New", Courier, monospace',
  },

  // Font sizes
  fontSize: {
    xs: '12px',     // 0.75rem
    sm: '14px',     // 0.875rem
    base: '16px',   // 1rem
    lg: '18px',     // 1.125rem
    xl: '20px',     // 1.25rem
    '2xl': '24px',  // 1.5rem
    '3xl': '30px',  // 1.875rem
    '4xl': '36px',  // 2.25rem
    '5xl': '48px',  // 3rem
    '6xl': '64px',  // 4rem
  },

  // Font weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },

  // Line heights
  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },

  // Letter spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

/**
 * SHADOWS
 * Elevation levels for depth perception
 */
export const shadows = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)',

  // Semantic shadows
  card: '0 4px 16px rgba(0, 0, 0, 0.1)',
  button: '0 2px 8px rgba(0, 0, 0, 0.12)',
  modal: '0 8px 24px rgba(0, 0, 0, 0.15)',
  dropdown: '0 4px 12px rgba(0, 0, 0, 0.1)',
  tooltip: '0 2px 8px rgba(0, 0, 0, 0.15)',

  // Inner shadows
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
  innerLg: 'inset 0 4px 8px rgba(0, 0, 0, 0.08)',
};

/**
 * BORDER RADIUS
 * Rounding values for corners
 */
export const radius = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  full: '9999px',   // Circular/pill shape

  // Semantic radius
  button: '8px',
  card: '12px',
  input: '8px',
  modal: '16px',
  badge: '9999px',
};

/**
 * TRANSITIONS
 * Animation timing and easing
 */
export const transitions = {
  // Duration
  duration: {
    instant: '0ms',
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },

  // Easing functions
  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },

  // Common transitions
  all: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  color: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

/**
 * Z-INDEX
 * Stacking order for layered elements
 */
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  backdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  notification: 1080,
};

/**
 * BREAKPOINTS
 * Responsive design breakpoints (match MUI defaults)
 */
export const breakpoints = {
  xs: '0px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
};

/**
 * COMPONENT-SPECIFIC TOKENS
 * Common patterns extracted from codebase
 */
export const components = {
  // Button styles (WCAG AAA touch targets: 44x44px minimum)
  button: {
    height: {
      sm: '44px',      // Increased from 32px for WCAG AAA compliance
      md: '48px',      // Increased from 40px for better usability
      lg: '56px',      // Increased from 48px for prominence
    },
    padding: {
      sm: '10px 20px', // Adjusted for new height
      md: '12px 24px', // Adjusted for new height
      lg: '16px 32px', // Adjusted for new height
    },
    minWidth: '44px',  // Ensure icon buttons meet minimum
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    borderRadius: radius.button,
    transition: transitions.all,
  },

  // Input styles (WCAG AAA touch targets: 44x44px minimum)
  input: {
    height: '44px',      // Increased from 40px for WCAG AAA compliance
    padding: '12px 16px', // Adjusted for new height
    fontSize: typography.fontSize.base,
    borderRadius: radius.input,
    borderColor: colors.border.main,
    borderWidth: '1px',
    focusBorderColor: colors.primary.main,
    focusBorderWidth: '2px',
  },

  // Card styles
  card: {
    padding: spacing.lg,
    borderRadius: radius.card,
    backgroundColor: colors.background.paper,
    boxShadow: shadows.card,
    border: `1px solid ${colors.border.light}`,
  },

  // Modal styles
  modal: {
    padding: spacing.xl,
    borderRadius: radius.modal,
    backgroundColor: colors.background.paper,
    boxShadow: shadows.modal,
    maxWidth: '600px',
  },

  // Loading spinner
  spinner: {
    size: {
      sm: '20px',
      md: '40px',
      lg: '60px',
    },
    color: colors.primary.main,
  },

  // Toast notification (from notifications.js)
  toast: {
    padding: '16px 20px',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: typography.fontWeight.medium,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    maxWidth: '500px',
    borderLeftWidth: '4px',
  },
};

/**
 * ACCESSIBILITY TOKENS
 * WCAG-compliant values
 */
export const a11y = {
  // Minimum touch target size (WCAG 2.1 Level AAA)
  touchTarget: {
    min: '44px',      // Minimum for AAA compliance
    recommended: '48px',
  },

  // Focus indicators
  focusRing: {
    width: '2px',
    offset: '2px',
    color: colors.primary.main,
    style: 'solid',
  },

  // Skip link (keyboard navigation)
  skipLink: {
    position: 'absolute',
    left: '-9999px',
    zIndex: zIndex.tooltip,
    padding: spacing.md,
    backgroundColor: colors.primary.main,
    color: colors.primary.contrast,
    textDecoration: 'none',
    borderRadius: radius.md,
  },
};

/**
 * UTILITY FUNCTIONS
 */

/**
 * Convert hex color to rgba with opacity
 * @param {string} hex - Hex color code
 * @param {number} alpha - Opacity (0-1)
 * @returns {string} rgba color string
 */
export function hexToRgba(hex, alpha = 1) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Create hover state color (darker for light colors, lighter for dark colors)
 * @param {string} color - Base color
 * @returns {string} Hover color
 */
export function getHoverColor(color) {
  // For primary colors, use pre-defined dark variant
  if (color === colors.primary.main) return colors.primary.dark;
  if (color === colors.secondary.main) return colors.secondary.dark;

  // Default: add overlay
  return `color-mix(in srgb, ${color} 90%, black)`;
}

/**
 * Get focus styles object (reusable)
 * @returns {object} MUI sx object for focus state
 */
export function getFocusStyles() {
  return {
    outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
    outlineOffset: a11y.focusRing.offset,
  };
}

/**
 * Create responsive value object for MUI
 * @param {object} values - { xs, sm, md, lg, xl }
 * @returns {object} Responsive object
 */
export function responsive(values) {
  return values;
}

// Default export with all tokens
export default {
  colors,
  spacing,
  typography,
  shadows,
  radius,
  transitions,
  zIndex,
  breakpoints,
  components,
  a11y,
  hexToRgba,
  getHoverColor,
  getFocusStyles,
  responsive,
};
